import getConfig from 'next/config';
import cx from 'classnames';
import {
  useContext, useEffect, useRef, useState,
} from 'react';
import { useRouter } from 'next/router';

import UserContext from 'utils/context';
import { ACCESS_TOKEN_KEY, getToken } from 'utils/api';

import styles from './styles.module.css';

const { publicRuntimeConfig } = getConfig();

const quickQuestions = [
  'How does PoolUp scoring work?',
  'What is Combo Prediction?',
  'How do private pools work?',
];

const initialMessages = [
  {
    role: 'assistant',
    content: 'Hi, I am PoolUp AI. ✨ Ask me about scoring, predictions, pools, tickets, or account features. 🎯',
  },
];

const guestMemoryStorageKey = 'poolup_ai_guest_memory';
const guestMemoryTtlMs = 2 * 60 * 60 * 1000;

const isEnabled = () => publicRuntimeConfig?.AI_ASSISTANT_ENABLED !== 'false';

const readAiResponse = async (response, onStatus) => {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/x-ndjson')) {
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'AI assistant request failed.');
    }
    return data;
  }

  if (!response.body) {
    throw new Error('AI assistant response stream is unavailable.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalResponse = null;
  let streamError = null;

  const readEvent = (line) => {
    if (!line.trim()) {
      return;
    }
    const event = JSON.parse(line);
    if (event.type === 'status') {
      onStatus({ stage: event.stage, message: event.message });
    } else if (event.type === 'response') {
      finalResponse = event.data;
    } else if (event.type === 'error') {
      streamError = event.message;
    }
  };

  let streamComplete = false;
  while (!streamComplete) {
    // eslint-disable-next-line no-await-in-loop
    const { value, done } = await reader.read();
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    lines.forEach(readEvent);
    if (done) {
      streamComplete = true;
    }
  }
  readEvent(buffer);

  if (streamError) {
    throw new Error(streamError);
  }
  if (!response.ok || !finalResponse) {
    throw new Error('AI assistant response was incomplete.');
  }
  return finalResponse;
};

const createGuestMemoryKey = () => {
  const uuid = window.crypto?.randomUUID?.()
    || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `web:guest:${uuid}`;
};

const loadGuestMemory = () => {
  const now = Date.now();
  try {
    const stored = JSON.parse(
      window.localStorage.getItem(guestMemoryStorageKey) || 'null',
    );
    if (stored?.memoryKey && stored?.expiresAt > now) {
      return stored.memoryKey;
    }
  } catch {
    // Ignore invalid stored data and create a fresh anonymous session below.
  }

  const memoryKey = createGuestMemoryKey();
  window.localStorage.setItem(
    guestMemoryStorageKey,
    JSON.stringify({
      memoryKey,
      expiresAt: now + guestMemoryTtlMs,
    }),
  );
  return memoryKey;
};

const AiAssistant = () => {
  const router = useRouter();
  const { user } = useContext(UserContext);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(initialMessages);
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [compactMobileLauncher, setCompactMobileLauncher] = useState(false);
  const [guestMemoryKey, setGuestMemoryKey] = useState(null);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const activeRequestRef = useRef(null);
  const memoryKey = user?.id ? `user:${user.id}` : guestMemoryKey;

  useEffect(() => () => {
    activeRequestRef.current?.abort();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, progress, open]);

  useEffect(() => {
    if (!user?.id) {
      setGuestMemoryKey(loadGuestMemory());
      return;
    }
    setGuestMemoryKey(null);
  }, [user?.id]);

  useEffect(() => {
    const isMobile = window.matchMedia('(max-width: 480px)').matches;
    const isHomePage = router.pathname === '/';

    if (!isMobile || !isHomePage) {
      setCompactMobileLauncher(isMobile);
      return undefined;
    }

    setCompactMobileLauncher(false);
    const compactTimer = window.setTimeout(() => {
      setCompactMobileLauncher(true);
    }, 5000);

    return () => window.clearTimeout(compactTimer);
  }, [router.pathname]);

  if (!isEnabled()) {
    return null;
  }

  const stopGeneration = () => {
    activeRequestRef.current?.abort();
    activeRequestRef.current = null;
    setLoading(false);
    setProgress(null);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const sendMessage = async (text, confirmedAction = null, baseMessages = messages) => {
    const message = text.trim();
    if (!message || loading || !memoryKey) {
      return;
    }

    const nextMessages = confirmedAction
      ? baseMessages
      : [...baseMessages, { role: 'user', content: message }];
    setMessages(nextMessages);
    if (!confirmedAction) {
      setValue('');
    }
    setLoading(true);
    setProgress({
      stage: 'request_received',
      message: confirmedAction
        ? 'Completing your PoolUp action...'
        : 'Processing your request...',
    });

    const requestController = new AbortController();
    activeRequestRef.current = requestController;

    try {
      const token = getToken(ACCESS_TOKEN_KEY);
      const response = await fetch('/api/ai-chat', {
        method: 'POST',
        signal: requestController.signal,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          message,
          user_id: user?.id || null,
          thread_id: memoryKey,
          memory_key: user?.id ? null : memoryKey,
          guest_session: !user?.id,
          confirmed_action: confirmedAction,
        }),
      });
      const data = await readAiResponse(response, setProgress);

      setMessages([
        ...nextMessages,
        {
          role: 'assistant',
          content: data.answer || 'PoolUp AI Assistant could not answer this request right now.',
          pendingAction: data.pending_action || null,
        },
      ]);
    } catch (error) {
      if (error.name === 'AbortError') {
        setMessages([
          ...nextMessages,
          {
            role: 'assistant',
            content: 'Response stopped. You can ask a new question.',
          },
        ]);
        return;
      }

      setMessages([
        ...nextMessages,
        {
          role: 'assistant',
          content: 'PoolUp AI Assistant is temporarily unavailable. Please try again later.',
        },
      ]);
    } finally {
      if (activeRequestRef.current === requestController) {
        activeRequestRef.current = null;
      }
      setLoading(false);
      setProgress(null);
    }
  };

  const submit = (event) => {
    event.preventDefault();
    if (loading) {
      stopGeneration();
      return;
    }
    sendMessage(value);
  };

  const confirmAction = (message, messageIndex) => {
    const confirmedMessages = messages.map((item, index) => (
      index === messageIndex ? { ...item, pendingAction: null } : item
    ));
    setMessages(confirmedMessages);
    sendMessage(message.content, message.pendingAction, confirmedMessages);
  };

  const messageClass = (role) => (role === 'user' ? styles.user : styles.bot);

  const openPanel = () => {
    setOpen(true);
    window.setTimeout(() => inputRef.current?.focus(), 120);
  };

  return (
    <section
      className={cx(styles.assistant, {
        [styles.mobileCompact]: compactMobileLauncher,
      })}
      aria-label="PoolUp AI Assistant"
    >
      {open && (
        <div className={styles.panel}>
          <header className={styles.header}>
            <div className={styles.identity}>
              <div className={styles.avatar}>AI</div>
              <div>
                <div className={styles.titleRow}>
                  <h2>PoolUp AI</h2>
                  <span>Powered</span>
                </div>
                <p>{user?.nickname ? 'Connected to your PoolUp session' : 'Public rules and gameplay help'}</p>
              </div>
            </div>
            <button
              type="button"
              className={styles.close}
              onClick={() => setOpen(false)}
              aria-label="Close PoolUp AI"
            >
              x
            </button>
          </header>

          <div className={styles.statusBar}>
            <span className={styles.statusDot} />
            <span>Ready to help with PoolUp</span>
          </div>

          <div className={styles.messages}>
            {messages.map((message, index) => (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={`${message.role}-${index}`}
                className={cx(styles.message, messageClass(message.role))}
              >
                {message.content}
                {message.pendingAction && (
                  <button
                    type="button"
                    className={styles.confirmAction}
                    onClick={() => confirmAction(message, index)}
                    disabled={loading}
                  >
                    Confirm
                  </button>
                )}
              </div>
            ))}
            {loading && progress && (
              <div
                className={cx(styles.message, styles.bot, styles.progress)}
                role="status"
                aria-live="polite"
              >
                <span className={styles.progressSpinner} aria-hidden="true" />
                <span className={styles.progressMessage}>{progress.message}</span>
                <span className={styles.progressDots} aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className={styles.quickQuestions}>
            {quickQuestions.map((question) => (
              <button
                type="button"
                key={question}
                onClick={() => sendMessage(question)}
                disabled={loading}
              >
                {question}
              </button>
            ))}
          </div>

          <form className={styles.form} onSubmit={submit}>
            <input
              ref={inputRef}
              value={value}
              onChange={(event) => setValue(event.target.value)}
              placeholder="Ask about PoolUp..."
            />
            <button
              type="submit"
              className={cx({ [styles.stop]: loading })}
              disabled={!loading && !value.trim()}
              aria-label={loading ? 'Stop response' : 'Send message'}
            >
              {loading ? 'Stop' : 'Send'}
            </button>
          </form>
        </div>
      )}

      {!open && (
        <button type="button" className={styles.launcher} onClick={openPanel}>
          <span className={styles.launcherIcon}>AI</span>
          <span className={styles.launcherText}>Ask PoolUp AI</span>
        </button>
      )}
    </section>
  );
};

export default AiAssistant;
